import { useState, type ChangeEvent, type FocusEvent } from 'react';

/** What a treatment start date field shows and how it reports edits. */
export type TreatmentStartEdit = {
    /** The date shown, `yyyy-MM-dd`, or '' for none. */
    value: string;
    /** True when the user left the field with a date half typed. */
    incomplete: boolean;
    onChange: (event: ChangeEvent<HTMLInputElement>) => void;
    onBlur: (event: FocusEvent<HTMLInputElement>) => void;
};

/**
 * Edits a stored treatment start date locally and saves it when the user
 * leaves the field with a complete date, so typing a date does not write one
 * partial date per keystroke. Until the user edits it, the field shows the
 * stored date. A stored date that changes, by this save or anybody else's,
 * discards the edit. A refused save goes back to what is stored, which is
 * what the failure message says is shown.
 */
export function useTreatmentStartEdit(
    stored: string | undefined,
    save: (next: string | undefined, onError: () => void) => void
): TreatmentStartEdit {
    const [edit, setEdit] = useState<string>();
    const [incomplete, setIncomplete] = useState(false);
    const [editOf, setEditOf] = useState(stored);
    if (editOf !== stored) {
        setEditOf(stored);
        setEdit(undefined);
        setIncomplete(false);
    }

    return {
        value: edit ?? stored ?? '',
        incomplete,
        onChange: (event) => {
            setIncomplete(false);
            setEdit(event.target.value);
        },
        onBlur: (event) => {
            // A date input reports an incomplete date as empty; that is not
            // the user clearing the start.
            if (event.target.validity.badInput) {
                setIncomplete(true);
                return;
            }
            // An untouched field saves nothing.
            if (edit === undefined) {
                return;
            }
            const next = edit || undefined;
            if (next !== stored) {
                save(next, () => setEdit(undefined));
            }
        },
    };
}
